import { defineStore } from 'pinia'
import { getUserInfo, setUserInfo, clearUserInfo, getAccessToken, setAccessToken } from '@/services/auth'
import { PERMISSIONS, ROLES, ROLE_PERMISSIONS } from '@/utils/permission'

export const useUserStore = defineStore('user', {
  state: () => ({
    userInfo: null,
    accessToken: null,
    isAuthenticated: false,
    isInitialized: false
  }),

  getters: {
    // 获取用户角色
    userRole: (state) => state.userInfo?.role || null,
    
    // 获取用户权限列表（必须与 router/index.js 中守卫逻辑一致：合并后端权限 + 角色默认权限）
    userPermissions: (state) => {
      if (!state.userInfo) return []

      let list = []

      if (state.userInfo.permissions && Array.isArray(state.userInfo.permissions) && state.userInfo.permissions.length > 0) {
        list = [...state.userInfo.permissions]
      }

      if (state.userInfo.custom_permissions && Array.isArray(state.userInfo.custom_permissions) && state.userInfo.custom_permissions.length > 0) {
        list = [...new Set([...list, ...state.userInfo.custom_permissions])]
      }

      if (state.userInfo.role && ROLE_PERMISSIONS[state.userInfo.role]) {
        list = [...new Set([...list, ...ROLE_PERMISSIONS[state.userInfo.role]])]
      }

      return list
    },
    
    // 检查用户是否是管理员
    isAdmin: (state) => state.userInfo?.role === ROLES.ADMIN,
    
    // 检查用户是否是教师
    isTeacher: (state) => state.userInfo?.role === ROLES.TEACHER,
    
    // 检查用户是否是学生
    isStudent: (state) => state.userInfo?.role === ROLES.STUDENT
  },

  actions: {
    // 初始化用户状态
    initialize() {
      const storedUser = getUserInfo()
      const storedToken = getAccessToken()
      
      if (storedUser && storedToken) {
        this.userInfo = storedUser
        this.accessToken = storedToken
        this.isAuthenticated = true
      }
    },
    
    // 设置用户信息
    setUser(userData, token) {
      this.userInfo = userData
      this.accessToken = token
      this.isAuthenticated = true
      
      // 保存到本地存储
      setUserInfo(userData)
      setAccessToken(token)
      
      // 触发认证状态变化事件
      window.dispatchEvent(new Event('auth-status-changed'))
    },
    
    // 清除用户信息
    logout() {
      this.userInfo = null
      this.accessToken = null
      this.isAuthenticated = false
      
      // 清除本地存储
      clearUserInfo()
      
      // 触发认证状态变化事件
      window.dispatchEvent(new Event('auth-status-changed'))
    },
    
    // 检查用户是否有指定权限
    hasPermission(permission) {
      if (!this.isAuthenticated) return false
      return this.userPermissions.includes(permission)
    },
    
    // 检查用户是否有任意一个指定权限
    hasAnyPermission(permissions) {
      if (!this.isAuthenticated) return false
      if (!Array.isArray(permissions)) permissions = [permissions]
      return permissions.some(perm => this.userPermissions.includes(perm))
    },
    
    // 检查用户是否有所有指定权限
    hasAllPermissions(permissions) {
      if (!this.isAuthenticated) return false
      if (!Array.isArray(permissions)) permissions = [permissions]
      return permissions.every(perm => this.userPermissions.includes(perm))
    },
    
    // 检查用户角色
    hasRole(role) {
      if (!this.isAuthenticated) return false
      return this.userInfo?.role === role
    },
    
    // 检查用户是否有任意一个指定角色
    hasAnyRole(roles) {
      if (!this.isAuthenticated) return false
      if (!Array.isArray(roles)) roles = [roles]
      return roles.includes(this.userInfo?.role)
    },
    
    // 检查用户是否具有所有指定的角色
    hasAllRoles(roles) {
      if (!this.isAuthenticated) return false
      if (!Array.isArray(roles)) roles = [roles]
      return roles.includes(this.userInfo?.role)
    },
    
    // 更新用户信息
    updateUserInfo(updatedInfo) {
      if (!this.userInfo) return
      
      this.userInfo = { ...this.userInfo, ...updatedInfo }
      setUserInfo(this.userInfo)
    },
    
    // 更新用户权限
    updateUserPermissions(permissions) {
      if (!this.userInfo) return
      
      this.userInfo.custom_permissions = permissions
      setUserInfo(this.userInfo)
    },
    
    // 获取用户可访问的路由
    getAccessibleRoutes(routes) {
      return routes.filter(route => {
        // 无需权限的路由
        if (!route.meta?.requiresPermission) {
          return true
        }
        
        // 检查权限
        if (typeof route.meta.requiresPermission === 'string') {
          return this.hasPermission(route.meta.requiresPermission)
        } else if (Array.isArray(route.meta.requiresPermission)) {
          return this.hasAnyPermission(route.meta.requiresPermission)
        }
        
        return false
      })
    }
  }
})